import { RoomJoinOffer } from "../../components/room/DiscoverableRoomBlock";
import { useSharedContentBack } from "../../domains/room/hooks/useSharedContentBack";
import { IconArticle, IconExternalLink } from "@tabler/icons-react";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import BookMarkFillIcon from "../../assets/icons/bookmark-fill.svg?react";
import BookMarkSimpleIcon from "../../assets/icons/bookmark-simple.svg?react";
import ShareV2Icon from "../../assets/icons/share-v2.svg?react";
import ContentHeader from "../../components/app/headers/ContentHeader";
import PlaylistPanel from "../../components/app/PlaylistPanel";
import ArticlePublicationCard from "../../components/cards/ArticlePublicationCard";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import SkeletonPublicationCard from "../../components/skeleton/SkeletonPublicationCard";
import { Avatar } from "../../components/ui/Avatar";
import { PromoCard } from "../../components/ui/PromoCard";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { getRoomName } from "../../domains/room/hooks/useRoomName";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { getRoomLogoUrl } from "../../domains/room/utils/getRoomLogoUrl";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { openExternalUrl } from "../../tools/open-external-url";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import { VideoPlayer } from "./VideoPlayer";
import { DesktopVideoPage } from "./desktop/DesktopVideoPage";
import { formatVideoDuration, useVideoPage } from "./useVideoPage";
import { formatShortDate } from "../../domains/app/utils/renderDate";

export default function VideoPage(props: {
  slug: string;
  roomId: string | null;
}): React.JSX.Element {
  if (isDesktopLayout()) {
    return <DesktopVideoPage slug={props.slug} roomId={props.roomId} />;
  }
  return <MobileVideoPage slug={props.slug} roomId={props.roomId} />;
}

function MobileVideoPage(props: {
  slug: string;
  roomId: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentUser } = useCurrentUser();
  const { activeLang } = useActiveLang();
  const { articleLink } = usePageLinks(props.roomId);

  const {
    video,
    currentRoom,
    relatedArticles,
    isSaved,
    toggleSaveToPlaylists,
    shareVideo,
    playlistPanel,
  } = useVideoPage(props);

  const handleBack = useSharedContentBack(props.roomId);

  const journalName = video?.journal?.name ?? null;
  const duration =
    video !== null && video.duration !== null
      ? formatVideoDuration(video.duration)
      : null;
  const dateText =
    video === null ? "" : formatShortDate(video.publication_date, activeLang);

  const associatedArticle =
    video !== null ? (video.associatedArticles.at(0) ?? null) : null;
  const externalLink = video?.externalLink ?? null;

  const canBookmark = currentUser !== null && video !== null;
  const canShare = currentUser !== null && video !== null;

  return (
    <ScrollableLayout
      background="var(--color-ds-white)"
      header={
        <ContentHeader
          // ContentHeader is md:hidden by default; force it visible on desktop
          // too so the resource page keeps a top bar with a back button.
          className="md:block"
          onBack={handleBack}
          actions={
            <>
              {canBookmark ? (
                isSaved ? (
                  <BookMarkFillIcon
                    className="cursor-pointer text-ds-black"
                    onClick={() => {
                      void playlistPanel.open(video.id);
                    }}
                  />
                ) : (
                  <BookMarkSimpleIcon
                    className="cursor-pointer text-ds-black"
                    onClick={() => {
                      void playlistPanel.open(video.id);
                    }}
                  />
                )
              ) : null}
              {canShare ? (
                <ShareV2Icon
                  className="cursor-pointer text-ds-black"
                  onClick={() => {
                    void shareVideo(video, currentUser, props.roomId);
                  }}
                />
              ) : null}
            </>
          }
        />
      }
    >
      <div className="flex min-h-full flex-col gap-6 bg-white pb-6 font-dm-sans">
        <div className="relative aspect-video w-full overflow-hidden bg-ds-slate-100">
          {video !== null && (
            <VideoPlayer video={video} failureView={{ kind: "native" }} />
          )}
        </div>

        <div className="flex flex-col gap-3 px-5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-0.75 text-[11px]/4 tracking-[-0.11px] text-ds-slate-600 whitespace-nowrap">
              <span className="font-medium">{t("Video")}</span>
              {duration !== null && <span>· {duration}</span>}
              {dateText !== "" && <span>· {dateText}</span>}
            </div>
            {currentRoom !== null && (
              <Avatar
                variant="image"
                size="md"
                imageFit="scale-down"
                src={getRoomLogoUrl(currentRoom)}
                alt={getRoomName(currentRoom, activeLang)}
                badge={null}
              />
            )}
          </div>

          {video !== null && (
            <h1 className="m-0 font-dm-sans font-bold text-xl/6 tracking-tight text-ds-black">
              {video.title}
            </h1>
          )}

          {journalName !== null && (
            <div className="flex items-center gap-0.75 text-[11px]/4 tracking-[-0.11px] font-normal text-ds-slate-600">
              <Trans
                i18nKey="In <name>{{name}}</name>"
                values={{ name: journalName }}
                components={{
                  name: <span className="font-medium text-ds-slate-950" />,
                }}
              />
            </div>
          )}
        </div>

        {/* Under the title: the reader sees what they were sent first. */}
        {props.roomId !== null && <RoomJoinOffer roomId={props.roomId} />}

        {(associatedArticle !== null || externalLink !== null) && (
          <div className="flex flex-col gap-3 px-5">
            {associatedArticle !== null && (
              <PromoCard
                leading={
                  <Avatar
                    variant="icon"
                    size="md"
                    icon={<IconArticle size={20} stroke={1.5} />}
                    badge={null}
                  />
                }
                title={t("Discover the digested article linked to this video")}
                description=""
                onClick={() => {
                  history.push(articleLink(associatedArticle));
                }}
              />
            )}
            {externalLink !== null && (
              <PromoCard
                leading={
                  <Avatar
                    variant="icon"
                    size="md"
                    icon={<IconExternalLink size={20} stroke={1.5} />}
                    badge={null}
                  />
                }
                title={t("Find out more about this video!")}
                description=""
                onClick={() => {
                  openExternalUrl(externalLink);
                }}
              />
            )}
          </div>
        )}

        {(relatedArticles === null || relatedArticles.length > 0) && (
          <div className="flex flex-col gap-3">
            <h2 className="m-0 px-5 font-dm-sans text-xl/6 font-bold tracking-tight text-ds-black">
              {t("Recommended publications")}
            </h2>
            <div className="flex flex-col divide-y divide-ds-slate-100 px-5 *:py-4">
              {relatedArticles === null
                ? Array.from({ length: 3 }).map((_, idx) => (
                    <SkeletonPublicationCard
                      showImage
                      key={idx}
                      showDescription
                    />
                  ))
                : relatedArticles.map((article) => (
                    <ArticlePublicationCard
                      roomId={props.roomId}
                      key={article.id}
                      article={article}
                      analyticsListName={AnalyticsItemList.ARTICLE_PAGE}
                      recommendation={null}
                      onClick={null}
                      className={null}
                      showImage={true}
                    />
                  ))}
            </div>
          </div>
        )}
      </div>

      {playlistPanel.state.status === "ready" && video !== null && (
        <PlaylistPanel
          isVisible
          contentToAdd={playlistPanel.state.content}
          onClose={playlistPanel.close}
          onSave={async ({
            add,
            remove,
          }: {
            add: string[];
            remove: string[];
          }) => {
            await toggleSaveToPlaylists(video, { add, remove });
          }}
        />
      )}
    </ScrollableLayout>
  );
}
